/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useState } from "react";
import { ITeam } from "../../../../interfaces/teams";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useEntityNavigation } from "@/hooks/useEntityNavigation";
import { debounce } from "lodash";
import { listTeams, showTeam, createTeam, updateTeam, updateTeamAddUser, deleteTeam } from "@/services";
import { updateValue } from "@/utils/updateValue";
import { useEntityQueryParams } from "@/hooks/useEntityQueryParams";
import { useEntitySearch } from "@/hooks/useEntitySearch";
import {
  notifyDeleteResults,
  runDeleteBatch,
} from "@/utils/deleteResults";

const DEFAULT: ITeam = {
  name: "",
  description: "",
  externalCode: "",
  idOutsourced: "",
  idUsers: []
};

export const useTeams = ({
  filter: filterInitial,
  disabledShowId,
  includes = [],
  disabledGetAll,
  idOutsourced,
  defaultLimit,
}: {
  disabledGetAll?: boolean;
  disabledShowId?: boolean;
  idOutsourced?: string;
  defaultLimit?: number;
  filter?: Record<string, any>;
  includes?: string[];
}) => {
  const { id, idOutsourced: idOutsourcedParam } = useParams();
  const [teams, setTeams] = useState<ITeam[]>([]);
  const [team, setTeam] = useState<ITeam>(DEFAULT);
  const { filter, setFilter, handleFilter, page, setPage, limit, setLimit } =
    useEntityQueryParams({ filter: {
    idOutsourced: idOutsourced,
  }, defaultLimit });
  const [totalItems, setTotalItems] = useState<number>(0);
  const [enableChangePositionMap, setEnableChangePositionMap] =
    useState<boolean>(false);
  // Equipe vive sempre dentro de uma terceirizada, e o voltar devolve para ela
  const idOutsourcedCurrent = idOutsourced || idOutsourcedParam;
  const { goAdd, goEdit, goBack } = useEntityNavigation({
    basePath: `/towermanagement/outsourcedes/${idOutsourcedCurrent}/teams`,
    pathBack: `/towermanagement/outsourcedes/${idOutsourcedCurrent}`,
  });

  const [loading, setLoading] = useState<boolean>(false);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleTeam = (key: string, value: any, custom?: (v: any) => any) => {
    setTeam((curr: any) => {
      let newData = updateValue(curr, key, value);
      if (custom) newData = custom(newData);
      return { ...curr, ...newData };
    });
  };

  const getAll = useCallback(
    debounce(
      ({
        page = 1,
        limit,
        filter,
      }: {
        page?: number;
        limit?: number;
        filter?: any;
      }) => {
        listTeams({
          includes: [],
          filter: filter,
          page,
          limit,
        })
          .then(({ teams: listTeams, limit, currentPage, totalItems }) => {
            setTeams(listTeams);
            
            setLimit(Number(limit));
            setTotalItems(Number(totalItems));
          })
          .finally(() => setLoading(false));
      },
      300
    ),
    []
  );

  const search = useEntitySearch({ list: listTeams, key: "teams", includes, filter });


  const get = ({ id }: { id: string | number }) => {
    setLoading(true);
    showTeam({ id })
      .then(({ team: teamsShow }) => {
        setTeam(teamsShow);
      })
      .finally(() => setLoading(false));
  };

  const sanitizeTeam = (t: ITeam) => {
    const copy = { ...t };
    if (!copy.location) delete (copy as any).location;
    return copy;
  };

  const create = () => {
    setLoading(true);
    createTeam({ team: sanitizeTeam(team) })
      .then(({ team: outsourcedCreate }) => {
        toast.success(`Equipe criado com sucesso!`);
        goEdit(outsourcedCreate.id!);
      })
      .catch(() => setLoading(false))
      .finally(() => setLoading(false));
  };

  const update = () => {
    setLoading(true);
    updateTeam({ id: team.id!, team })
      .then(({ team: teamUpdate }) => {
        toast.success(`Equipe atualizado com sucesso!`);
        goEdit(teamUpdate.id!);
      })
      .finally(() => setLoading(false));
  };

  const addUser = ({ idUser }: { idUser: string }) => {
    setLoading(true);
    updateTeamAddUser({ id: team.id!, team: { idUser } })
      .then(() => {
        toast.success(`Usuario adicionado a equipe com sucesso!`);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!disabledGetAll) getAll({ page, limit, filter });
  }, [page, limit, filter]);

  useEffect(() => {
    setFilter(filterInitial);
  }, [filterInitial]);

  useEffect(() => {
    if (id && !disabledShowId) {
      if (id !== "new") get({ id: id });
      return;
    }
  }, [id]);

  useEffect(() => {
    handleTeam("idOutsourced", idOutsourcedParam);
  }, [idOutsourcedParam]);

  const deleteMany = async (ids: (string | number)[]) => {
    if (!ids?.length) return;
    setLoading(true);

    try {
      const results = await runDeleteBatch(ids, (entityId) =>
        deleteTeam({ id: entityId }),
      );

      notifyDeleteResults(results, {
        singular: "Equipe",
        plural: "equipes",
        gender: "f",
      });
    } catch {
      // erro sem resposta do servidor (rede/timeout): o interceptor já avisou
    } finally {
      setLoading(false);
      getAll({ page, limit, filter });
    }
  };

  return {
    deleteMany,
    teams,
    team,
    loading,
    setPage,
    setLimit,
    page,
    limit,
    goEdit,
    goBack,
    goAdd,
    handleTeam,
    create,
    id,
    update,
    enableChangePositionMap,
    setEnableChangePositionMap,
    totalItems,
    search,
    handleFilter,
    addUser
  };
};
